import {
  Badge,
  BadgeProps,
  HStack,
  Text,
  TextProps,
  Tooltip,
} from "@chakra-ui/react";
import {
  MarketingAdSet,
  MarketingCampaign,
} from "common/interfaces/KYDMarketing";

export const colorForStatus = (
  status: MarketingCampaign["status"] | MarketingAdSet["status"]
) => {
  if (status === "ACTIVE") return "blue.100";
  if (status === "PAUSED") return "yellow.100";
  if (status === "ARCHIVED") return "gray.400";
  if (status === "TO_DELETE") return "gray.100";
  if (status === "DELETED") return "gray.100";
  if (status === "FROZEN") return "gray.100";
  return "gray.400";
};

export const getTextForStatus = (
  status: MarketingCampaign["status"] | MarketingAdSet["status"] | string
) => {
  if (status === "FROZEN")
    return "KYD freezes these objects when they were changed from the ads manager side and use features not supported by KYD ads manager";
  return status.toLowerCase();
};

const badgeStyles: BadgeProps = {
  borderRadius: "0.625rem",
  bg: "#AEDFAA",
  display: "flex",
  px: 2,
  justifyContent: "center",
  height: 4,
  alignItems: "center",
  gap: "0.625rem",
  alignSelf: "stretch",
  cursor: "text",
  userSelect: "text" as const,
  _hover: { opacity: 0.8 },
};

const textStyles: TextProps = {
  color: "#1F531A",
  fontFamily: "Work Sans",
  fontSize: "xx-small",
  fontWeight: 500,
  lineHeight: "100%",
};

const colorSchemeForStatus = (
  status: MarketingCampaign["status"] | MarketingAdSet["status"]
) => {
  if (status === "ACTIVE") return "green.100";
  if (status === "PAUSED") return "yellow.100";
  if (status === "ARCHIVED") return "gray.400";
  if (status === "TO_DELETE") return "gray.100";
  if (status === "DELETED") return "gray.100";
};

export const StatusBadge = ({
  status,
}: {
  status: MarketingCampaign["status"] | MarketingAdSet["status"];
}) => {
  const statusText = getTextForStatus(status);
  const isPerformance = performance != null;

  const handleTextSelect = (e: React.MouseEvent) => {
    e.preventDefault();
    const selection = window.getSelection();
    if (selection) {
      const range = document.createRange();
      range.selectNodeContents(e.currentTarget);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  };

  const label = status?.toUpperCase() ?? "";

  const badge = (
    <Badge
      {...badgeStyles}
      colorScheme={colorSchemeForStatus(
        status as MarketingCampaign["status"] | MarketingAdSet["status"]
      )}
      bg={colorForStatus(status)}
    >
      <HStack gap="0.625rem">
        <Text
          {...textStyles}
          textTransform={isPerformance ? "none" : "capitalize"}
          onDoubleClick={handleTextSelect}
        >
          {label}
        </Text>
      </HStack>
    </Badge>
  );

  if (isPerformance) return badge;

  return (
    <Tooltip
      label={`${status}:\n${statusText}`}
      hasArrow
      placement="top"
      bg="gray.700"
      color="white"
      fontSize="sm"
    >
      {badge}
    </Tooltip>
  );
};
